/* ============================================================
   HYDRIFIC — DROPLET CART CONTROLLER
   ------------------------------------------------------------
   Sits on top of Dawn's own <cart-drawer> element. It does NOT
   reimplement the cart: Dawn's cart.js / cart-drawer.js keep owning the
   AJAX Cart API, section rendering and cart state. Shopify's returned
   cart data stays the single source of truth.

   What this adds:
     1. Every cart link/control in the theme opens the drawer instead of
        navigating to /cart.
     2. Escape and backdrop close reliably, even before focus has landed
        inside the panel.
     3. Scroll lock that does not shift the page horizontally.
     4. Focus restore to whatever opened the drawer.
     5. An optional freshness refresh when the drawer is opened.
     6. Direct /cart visits open the drawer over the page.

   All listeners are delegated on `document`, so section re-renders in
   the theme editor cannot produce duplicates. The module also guards
   against being evaluated twice.
   ============================================================ */
(function () {
  'use strict';

  if (window.__dropletCart) return;

  var CFG = window.dropletCartConfig || {};
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* --------------------------------------------------------- helpers */

  function drawer() {
    return document.querySelector('cart-drawer');
  }

  function isOpen() {
    var d = drawer();
    return !!(d && d.classList.contains('active'));
  }

  /* A cart link is /cart or /cart?… — never /cart/add, /cart/change,
     /cart/update, /cart/clear (those are API endpoints and form actions). */
  function isCartHref(href) {
    if (!href) return false;
    var path;
    try {
      /* base on the full current URL, not origin — origin is "null" for
         file:// and some sandboxed contexts, which would throw */
      path = new URL(href, document.baseURI || window.location.href).pathname;
    } catch (e) {
      return false;
    }
    path = path.replace(/\/+$/, '');
    return /(^|\/)cart$/.test(path);
  }

  /* Modified clicks keep native behaviour so "open in new tab" still works. */
  function isPlainClick(e) {
    return !(e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey);
  }

  /* --------------------------------------------------------- open/close */

  function open(trigger) {
    var d = drawer();
    if (!d || typeof d.open !== 'function') return false;
    d.open(trigger);
    maybeRefresh();
    return true;
  }

  function close() {
    var d = drawer();
    if (d && typeof d.close === 'function') d.close();
  }

  /* --------------------------------------------------------- 1. link capture */

  document.addEventListener(
    'click',
    function (e) {
      if (!isPlainClick(e)) return;

      var el = e.target.closest
        ? e.target.closest('a[href], [data-droplet-cart-open]')
        : null;
      if (!el) return;

      /* explicit opt-out for anything that must reach /cart for real */
      if (el.hasAttribute('data-droplet-cart-allow')) return;

      var explicit = el.hasAttribute('data-droplet-cart-open');
      if (!explicit && !isCartHref(el.getAttribute('href'))) return;

      /* If the drawer isn't on this page, let the link do its job. */
      if (!drawer()) return;

      e.preventDefault();
      open(el);
    },
    false
  );

  /* --------------------------------------------------------- 2. escape */

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' && e.code !== 'Escape') return;
    if (!isOpen()) return;
    close();
  });

  /* --------------------------------------------------------- 3. scroll lock
     Dawn toggles `.overflow-hidden` on <body>. On desktop that removes the
     scrollbar and the page jumps sideways, which reads as a layout bug.
     Compensate with the exact scrollbar width. */

  var lockedPad = false;

  function applyLock(on) {
    var body = document.body;
    if (on && !lockedPad) {
      var sb = window.innerWidth - document.documentElement.clientWidth;
      if (sb > 0) {
        body.dataset.dropletPadPrev = body.style.paddingRight || '';
        body.style.paddingRight = sb + 'px';
        lockedPad = true;
      }
    } else if (!on && lockedPad) {
      body.style.paddingRight = body.dataset.dropletPadPrev || '';
      delete body.dataset.dropletPadPrev;
      lockedPad = false;
    }
  }

  /* --------------------------------------------------------- 4. focus restore
     Dawn stores the trigger on the element and removeTrapFocus returns to it.
     This is a safety net for the paths that open the drawer without one. */

  var lastTrigger = null;

  /* --------------------------------------------------------- state watcher */

  var observed = null;

  function watch() {
    var d = drawer();
    if (!d || d === observed) return;
    observed = d;

    var wasOpen = d.classList.contains('active');

    new MutationObserver(function () {
      var nowOpen = d.classList.contains('active');
      if (nowOpen === wasOpen) return;
      wasOpen = nowOpen;

      applyLock(nowOpen);

      if (nowOpen) {
        lastTrigger = document.activeElement;
      } else if (lastTrigger && typeof lastTrigger.focus === 'function' && lastTrigger.isConnected) {
        try {
          lastTrigger.focus({ preventScroll: true });
        } catch (err) {
          /* focus restore is a nicety; never let it throw into the console */
        }
        lastTrigger = null;
      }
    }).observe(d, { attributes: true, attributeFilter: ['class'] });
  }

  /* --------------------------------------------------------- 5. freshness
     The drawer markup is server-rendered and Dawn re-renders it on every
     mutation made in this tab, so it only goes stale when the cart changed
     elsewhere. Refresh is therefore conservative: skipped while a cart
     mutation is in flight or has just landed, and it never touches the DOM
     unless the fetch fully succeeds. */

  var lastMutation = 0;
  var refreshing = false;

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
      lastMutation = Date.now();
    });
  }
  document.addEventListener('submit', function (e) {
    if (e.target && e.target.matches && e.target.matches('form[action*="/cart"]')) {
      lastMutation = Date.now();
    }
  });

  function maybeRefresh() {
    if (CFG.refreshOnOpen === false) return;
    if (refreshing) return;
    if (Date.now() - lastMutation < 2500) return;

    var d = drawer();
    if (!d) return;

    refreshing = true;
    fetch(root + '?sections=cart-drawer,cart-icon-bubble', {
      headers: { Accept: 'application/json' }
    })
      .then(function (r) {
        if (!r.ok) throw new Error('sections ' + r.status);
        return r.json();
      })
      .then(function (data) {
        /* Bail out rather than clobber if anything changed mid-flight. */
        if (Date.now() - lastMutation < 2500) return;
        if (!isOpen()) return;
        if (!data || !data['cart-drawer']) return;

        var incoming = new DOMParser()
          .parseFromString(data['cart-drawer'], 'text/html')
          .querySelector('#CartDrawer');
        var current = document.querySelector('#CartDrawer');
        if (!incoming || !current) return;

        /* Only swap when the rendered contents actually differ, so we don't
           destroy focus or an in-progress quantity edit for nothing. */
        if (incoming.innerHTML.length === current.innerHTML.length) return;
        current.innerHTML = incoming.innerHTML;

        var bubbleHTML = data['cart-icon-bubble'];
        var bubble = document.getElementById('cart-icon-bubble');
        if (bubbleHTML && bubble) {
          var nb = new DOMParser()
            .parseFromString(bubbleHTML, 'text/html')
            .querySelector('.shopify-section');
          if (nb) bubble.innerHTML = nb.innerHTML;
        }
      })
      .catch(function () {
        /* Stale-but-working beats broken. Leave the rendered cart alone. */
      })
      .then(function () {
        refreshing = false;
      });
  }

  /* --------------------------------------------------------- 6. /cart page */

  function handleCartPage() {
    if (CFG.cartPageMode !== 'drawer') return;
    if (!/(^|\/)cart\/?$/.test(window.location.pathname)) return;
    if (!drawer()) return;
    /* No redirect, so no possibility of a loop; the styled cart page stays
       underneath as the no-JS fallback. */
    setTimeout(function () {
      open(null);
    }, 60);
  }

  /* --------------------------------------------------------- boot */

  function boot() {
    watch();
    handleCartPage();
  }

  /* Sections can be swapped in the theme editor — re-attach the observer to
     whatever <cart-drawer> exists now. Delegated listeners need no rebinding. */
  document.addEventListener('shopify:section:load', function () {
    observed = null;
    watch();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  window.__dropletCart = { open: open, close: close, isOpen: isOpen };
})();
